<!--
  顶部工具栏组件
  职责：展示工程名称、操作按钮（新建/打开/设置/发布/运行/返回）、用户登录状态
-->
<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import {
  NButton,
  NSpace,
  NText,
} from 'naive-ui'
import AuthAvatar from '@/components/AuthAvatar.vue'
import { useAuthStore } from '@/features/auth'
import { useProjectLoadingStore } from '@/store/projectLoading'
import { useWorkspaceStore } from '@/store/workspace'
import { useThemeStore } from '@/store/theme'
import { APP_DISPLAY_VERSION } from '@/config/appVersion'

const auth = useAuthStore()
const projectLoading = useProjectLoadingStore()
const workspace = useWorkspaceStore()
const themeStore = useThemeStore()
const { active: loadingActive, status: loadingStatus, mode: loadingMode } = storeToRefs(projectLoading)

const saveButtonLoading = computed(() => loadingActive.value && loadingMode.value === 'inline')
const isFullscreen = ref(false)
const projectResolutionText = computed(() => {
  const project = workspace.project
  if (!project) return ''
  const width = Number(project.screenHash?.width || 0)
  const height = Number(project.screenHash?.height || 0)
  if (!width || !height) return ''
  return `${width}*${height}`
})
const projectTitleText = computed(() => {
  const baseTitle = workspace.displayTitle || '当前工程'
  const resolution = projectResolutionText.value ? `（${projectResolutionText.value}）` : ''
  const dirtyMark = workspace.hasPendingChanges ? '*' : ''
  return `${baseTitle}${resolution}${dirtyMark} - 橙易 ${APP_DISPLAY_VERSION}`
})

const props = withDefaults(defineProps<{
  testRunning?: boolean
  scenePaneCollapsed?: boolean
  resourcePaneCollapsed?: boolean
  portraitLayout?: boolean
  canUndo?: boolean
  canRedo?: boolean
}>(), {
  testRunning: false,
  scenePaneCollapsed: false,
  resourcePaneCollapsed: false,
  portraitLayout: false,
  canUndo: false,
  canRedo: false,
})

const emit = defineEmits<{
  /** 保存工程 */
  saveProject: []
  /** 撤销最近一次编辑 */
  undo: []
  /** 重做最近一次撤销 */
  redo: []
  /** 切换左侧剧情树显隐 */
  toggleScenePane: []
  /** 切换底部资源区显隐 */
  toggleResourcePane: []
  /** 打开素材库 */
  materialLibrary: []
  /** 打开文本导入 */
  textImport: []
  /** 打开设置 */
  settings: []
  /** 发布 */
  publish: []
  /** 运行预览 */
  run: []
  /** 返回启动页 */
  goStartup: []
}>()

function syncFullscreenState(): void {
  isFullscreen.value = Boolean(document.fullscreenElement)
  if (!document.fullscreenElement) {
    screen.orientation?.unlock?.()
  }
}

async function lockPortraitFullscreenIfNeeded(): Promise<void> {
  if (!props.portraitLayout) return
  const viewport = window.visualViewport
  const width = Math.round(viewport?.width ?? window.innerWidth)
  const height = Math.round(viewport?.height ?? window.innerHeight)
  if (width >= height) return
  try {
    await screen.orientation?.lock?.('portrait-primary')
  } catch {
    // 部分移动浏览器不允许锁方向，保留全屏本身即可。
  }
}

async function toggleFullscreen(): Promise<void> {
  if (document.fullscreenElement) {
    await document.exitFullscreen()
    return
  }
  await document.documentElement.requestFullscreen({ navigationUI: 'hide' })
  await lockPortraitFullscreenIfNeeded()
}

onMounted(() => {
  syncFullscreenState()
  document.addEventListener('fullscreenchange', syncFullscreenState)
})

onBeforeUnmount(() => {
  document.removeEventListener('fullscreenchange', syncFullscreenState)
})
</script>

<template>
  <header class="top-bar" :class="{ 'top-bar--portrait': portraitLayout }">
    <div class="top-bar-left">
      <div class="project-title-wrap">
        <div class="project-title">{{ projectTitleText }}</div>
      </div>
    </div>

    <div class="top-actions">
      <n-space align="center" :wrap="false" :size="6" class="top-action-group">
        <n-button
          size="small"
          quaternary
          circle
          class="top-icon-btn"
          title="返回启动页"
          aria-label="返回启动页"
          @click="emit('goStartup')"
        >
          <span class="toolbar-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false">
              <path d="M4 11.5 12 5l8 6.5" />
              <path d="M6.5 10.5V19h11v-8.5" />
              <path d="M10 19v-5h4v5" />
            </svg>
          </span>
        </n-button>
        <n-button
          size="small"
          quaternary
          circle
          class="top-icon-btn"
          title="文本导入"
          aria-label="文本导入"
          @click="emit('textImport')"
        >
          <span class="toolbar-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false">
              <path d="M5.5 4.5h9l4 4v11h-13z" />
              <path d="M14.5 4.5v4h4" />
              <path d="M8.5 11h7" />
              <path d="M8.5 14h7" />
              <path d="M8.5 17h4.5" />
            </svg>
          </span>
        </n-button>
        <n-button
          size="small"
          quaternary
          circle
          class="top-icon-btn top-history-btn"
          title="撤销 Ctrl+Z"
          aria-label="撤销"
          :disabled="!props.canUndo"
          @click="emit('undo')"
        >
          <span class="toolbar-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false">
              <path d="M9 7 5 11l4 4" />
              <path d="M5 11h9.5a4.5 4.5 0 1 1 0 9H12" />
            </svg>
          </span>
        </n-button>
        <n-button
          size="small"
          quaternary
          circle
          class="top-icon-btn top-history-btn"
          title="重做 Ctrl+Y / Ctrl+Shift+Z"
          aria-label="重做"
          :disabled="!props.canRedo"
          @click="emit('redo')"
        >
          <span class="toolbar-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false">
              <path d="m15 7 4 4-4 4" />
              <path d="M19 11H9.5a4.5 4.5 0 1 0 0 9H12" />
            </svg>
          </span>
        </n-button>
        <n-button
          size="small"
          quaternary
          circle
          class="top-icon-btn"
          title="设置"
          aria-label="设置"
          @click="emit('settings')"
        >
          <span class="toolbar-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false">
              <path d="M10.3 4.4h3.4l.5 2.1c.5.2 1 .4 1.4.7l2-.7 1.7 3-1.6 1.4c.1.7.1 1.4 0 2.1l1.6 1.4-1.7 3-2-.7c-.4.3-.9.5-1.4.7l-.5 2.1h-3.4l-.5-2.1c-.5-.2-1-.4-1.4-.7l-2 .7-1.7-3L6.3 13c-.1-.7-.1-1.4 0-2.1L4.7 9.5l1.7-3 2 .7c.4-.3.9-.5 1.4-.7l.5-2.1Z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          </span>
        </n-button>
        <n-button
          size="small"
          quaternary
          circle
          class="top-icon-btn"
          :title="themeStore.mode === 'dark' ? '切换浅色' : '切换深色'"
          :aria-label="themeStore.mode === 'dark' ? '切换浅色' : '切换深色'"
          @click="themeStore.toggle()"
        >
          <span class="toolbar-icon" aria-hidden="true">
            <svg v-if="themeStore.mode === 'dark'" viewBox="0 0 24 24" focusable="false">
              <circle cx="12" cy="12" r="4.2" />
              <path d="M12 3.8V2.6" />
              <path d="M12 21.4v-1.2" />
              <path d="M3.8 12H2.6" />
              <path d="M21.4 12h-1.2" />
              <path d="m6.2 6.2-.9-.9" />
              <path d="m18.7 18.7-.9-.9" />
              <path d="m17.8 6.2.9-.9" />
              <path d="m5.3 18.7.9-.9" />
            </svg>
            <svg v-else viewBox="0 0 24 24" focusable="false">
              <path class="toolbar-icon-fill" d="M17.6 15.2A6.7 6.7 0 0 1 8.8 6.4 7.2 7.2 0 1 0 17.6 15.2Z" />
            </svg>
          </span>
        </n-button>
        <n-button
          size="small"
          quaternary
          circle
          class="top-icon-btn"
          :title="isFullscreen ? '退出全屏编辑' : '全屏编辑'"
          :aria-label="isFullscreen ? '退出全屏编辑' : '全屏编辑'"
          @click="toggleFullscreen"
        >
          <span class="toolbar-icon" aria-hidden="true">
            <svg v-if="!isFullscreen" viewBox="0 0 24 24" focusable="false">
              <path d="M8.5 4.5H4.5v4" />
              <path d="M4.5 4.5 9.5 9.5" />
              <path d="M15.5 4.5h4v4" />
              <path d="M19.5 4.5 14.5 9.5" />
              <path d="M8.5 19.5H4.5v-4" />
              <path d="M4.5 19.5 9.5 14.5" />
              <path d="M15.5 19.5h4v-4" />
              <path d="M19.5 19.5 14.5 14.5" />
            </svg>
            <svg v-else viewBox="0 0 24 24" focusable="false">
              <path d="M9.5 4.5v5h-5" />
              <path d="M9.5 9.5 4.5 4.5" />
              <path d="M14.5 4.5v5h5" />
              <path d="M14.5 9.5 19.5 4.5" />
              <path d="M9.5 19.5v-5h-5" />
              <path d="M9.5 14.5 4.5 19.5" />
              <path d="M14.5 19.5v-5h5" />
              <path d="M14.5 14.5 19.5 19.5" />
            </svg>
          </span>
        </n-button>
        <div v-if="!portraitLayout" class="layout-toggle-group" aria-label="面板显隐">
          <button
            type="button"
            class="layout-toggle-btn top-plain-icon-btn"
            :class="{
              'layout-toggle-btn--active': !props.scenePaneCollapsed,
              'layout-toggle-btn--collapsed': props.scenePaneCollapsed,
            }"
            :title="props.scenePaneCollapsed ? '展开剧情树' : '折叠剧情树'"
            :aria-label="props.scenePaneCollapsed ? '展开剧情树' : '折叠剧情树'"
            @click="emit('toggleScenePane')"
          >
            <span class="toolbar-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" focusable="false">
                <rect x="4.5" y="6" width="15" height="12" rx="1.4" />
                <rect class="layout-toggle-fill" x="6.5" y="8" width="4.6" height="8" rx="0.7" />
              </svg>
            </span>
          </button>
          <button
            type="button"
            class="layout-toggle-btn top-plain-icon-btn"
            :class="{
              'layout-toggle-btn--active': !props.resourcePaneCollapsed,
              'layout-toggle-btn--collapsed': props.resourcePaneCollapsed,
            }"
            :title="props.resourcePaneCollapsed ? '展开资源区' : '折叠资源区'"
            :aria-label="props.resourcePaneCollapsed ? '展开资源区' : '折叠资源区'"
            @click="emit('toggleResourcePane')"
          >
            <span class="toolbar-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" focusable="false">
                <rect x="4.5" y="6" width="15" height="12" rx="1.4" />
                <rect class="layout-toggle-fill" x="6.5" y="13.3" width="11" height="2.7" rx="0.6" />
              </svg>
            </span>
          </button>
        </div>
      </n-space>
    </div>

    <div class="top-bar-right">
      <n-button size="small" secondary @click="emit('materialLibrary')">素材库</n-button>
      <n-button
        size="small"
        secondary
        :loading="saveButtonLoading"
        :title="saveButtonLoading ? (loadingStatus || '正在保存…') : '保存'"
        @click="emit('saveProject')"
      >
        {{ saveButtonLoading ? '保存中' : '保存' }}
      </n-button>
      <n-button size="small" secondary @click="emit('publish')">发布</n-button>
      <n-button
        size="small"
        circle
        :type="props.testRunning ? 'error' : 'primary'"
        class="top-run-toggle"
        :title="props.testRunning ? '停止测试' : '开始测试'"
        :aria-label="props.testRunning ? '停止测试' : '开始测试'"
        @click="emit('run')"
      >
        <span class="toolbar-icon run-toggle-svg" aria-hidden="true">
          <svg v-if="props.testRunning" viewBox="0 0 24 24" focusable="false">
            <rect x="7" y="7" width="10" height="10" />
          </svg>
          <svg v-else viewBox="0 0 24 24" focusable="false">
            <path class="toolbar-icon-fill" d="M8 5.5v13l10-6.5z" />
          </svg>
        </span>
      </n-button>
      <template v-if="auth.loggedIn">
        <div class="user-trigger user-trigger--readonly">
          <AuthAvatar
            :key="`${auth.uid}-${auth.avatarUrl}`"
            :src="auth.avatarUrl || undefined"
            :letter="auth.avatarLetter"
            :alt="auth.displayName || '用户头像'"
            :size="portraitLayout ? 26 : 30"
          />
          <n-text depth="2">{{ auth.displayName }}</n-text>
        </div>
      </template>
      <n-text v-else depth="3" class="user-offline">未登录</n-text>
    </div>
  </header>
</template>

<style scoped>
.top-bar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(max-content, 1fr);
  align-items: center;
  gap: var(--app-space-md);
  padding: var(--app-space-sm) var(--app-space-lg);
  background: var(--theme-bg-surface);
  border-bottom: 1px solid var(--theme-border-strong);
  flex-shrink: 0;
}

.top-bar-left {
  display: flex;
  align-items: center;
  gap: var(--app-space-lg);
  justify-self: stretch;
  min-width: 0;
}

.project-title-wrap {
  display: flex;
  align-items: baseline;
  gap: var(--app-space-md);
  flex: 1 1 auto;
  min-width: 0;
}

.project-title {
  flex: 1 1 auto;
  min-width: 0;
  font-size: var(--app-font-size-md);
  font-weight: 600;
  color: var(--theme-text-heading);
  line-height: var(--app-line-height-tight);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.top-actions {
  display: flex;
  align-items: center;
  gap: var(--app-space-lg);
  justify-self: center;
  min-width: 0;
  overflow: hidden;
}

.top-action-group {
  flex-shrink: 0;
  padding: 0;
  background: transparent;
}

.top-action-group :deep(.n-space) {
  flex-wrap: nowrap !important;
}

.top-actions :deep(.n-button__content),
.top-bar-right :deep(.n-button__content) {
  font-size: var(--app-font-size-sm);
}

.top-actions {
  overflow-x: auto;
  overflow-y: hidden;
}

.top-actions :deep(.n-button),
.top-bar-right :deep(.n-button) {
  flex-shrink: 0;
  margin: 0;
  --n-height-small: var(--app-control-height-sm);
  --n-padding: 0 var(--app-space-md);
  --n-border: 1px solid var(--theme-border);
  --n-border-hover: 1px solid var(--theme-border-strong);
  --n-border-pressed: 1px solid var(--theme-border-strong);
  --n-color: var(--theme-bg-panel);
  --n-color-hover: var(--theme-bg-hover);
  --n-color-pressed: var(--theme-bg-active);
  --n-text-color: var(--theme-text-primary);
  --n-text-color-hover: var(--theme-text-heading);
  --n-text-color-pressed: var(--theme-text-heading);
  --n-border-radius: 0;
}

.top-bar-right :deep(.top-run-toggle) {
  --n-border-radius: 999px;
  --n-width: 26px;
  --n-padding: 0;
}

.top-actions :deep(.top-icon-btn) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  --n-width: 34px;
  --n-height-small: 34px;
  --n-padding: 0;
  --n-border: 1px solid transparent;
  --n-border-hover: 1px solid transparent;
  --n-border-pressed: 1px solid transparent;
  --n-color: transparent;
  --n-color-hover: color-mix(in srgb, var(--theme-accent) 10%, var(--theme-bg-panel));
  --n-color-pressed: color-mix(in srgb, var(--theme-accent) 16%, var(--theme-bg-panel));
  --n-text-color: var(--theme-text-secondary);
  --n-text-color-hover: var(--theme-accent);
  --n-text-color-pressed: var(--theme-accent);
  --n-border-radius: 999px;
}

.top-actions :deep(.top-icon-btn .n-button__content) {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  line-height: 0;
}

.top-actions :deep(.top-history-btn) {
  --n-width: 34px;
}

.top-actions :deep(.top-history-btn.n-button--disabled) {
  --n-color-disabled: transparent;
  --n-border-disabled: 1px solid transparent;
  --n-text-color-disabled: var(--theme-text-muted);
  color: var(--theme-text-muted);
}

.layout-toggle-group {
  display: flex;
  align-items: center;
  gap: 1px;
  padding: 0;
  height: 34px;
  background: transparent;
}

.layout-toggle-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  color: var(--theme-text-secondary);
  background: transparent;
  cursor: pointer;
}

.top-plain-icon-btn:hover,
.top-plain-icon-btn:focus-visible {
  color: var(--theme-accent);
  background: color-mix(in srgb, var(--theme-accent) 10%, var(--theme-bg-panel));
}

.layout-toggle-btn--collapsed {
  color: var(--theme-text-secondary);
  background: transparent;
}

.layout-toggle-btn:focus-visible {
  outline: 1px solid var(--theme-accent);
  outline-offset: -1px;
}

.toolbar-icon {
  display: inline-flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  flex: 0 0 24px;
  line-height: 0;
  vertical-align: middle;
}

.toolbar-icon svg {
  display: block;
  width: 23px;
  height: 23px;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  fill: none;
}

.toolbar-icon-fill {
  fill: currentColor;
  stroke: none;
}

.layout-toggle-fill {
  fill: currentColor;
  stroke: none;
  opacity: 0.5;
}

.layout-toggle-btn--active .layout-toggle-fill {
  opacity: 0.95;
}

.layout-toggle-btn--collapsed .layout-toggle-fill {
  opacity: 0.28;
}

.run-toggle-svg {
  width: 18px;
  height: 18px;
  flex-basis: 18px;
}

.run-toggle-svg svg {
  width: 18px;
  height: 18px;
  stroke-width: 0;
  fill: currentColor;
}

.top-bar-right {
  --top-bar-right-gap: var(--app-space-md);
  display: flex;
  align-items: center;
  justify-self: end;
  padding-left: var(--app-space-xs);
  border-left: 0;
  flex-shrink: 0;
  gap: var(--top-bar-right-gap);
}

.user-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 34px;
  padding: 0 var(--app-space-sm);
  border: 0;
  background: transparent;
  line-height: 1;
}

.user-trigger--readonly {
  cursor: default;
}

.top-actions :deep(.n-button--quaternary-type),
.top-bar-right :deep(.n-button--quaternary-type) {
  --n-border: 1px solid transparent;
  --n-color: transparent;
  --n-color-hover: transparent;
  --n-color-pressed: transparent;
  --n-text-color: var(--theme-text-secondary);
}

@media (max-width: 1200px) {
  .top-bar {
    gap: var(--app-space-sm);
  }

  .top-bar-left {
    flex-basis: auto;
  }

}

@media (max-width: 1180px) and (orientation: landscape) {
  .top-bar {
    gap: var(--app-space-xs);
    padding: 4px var(--app-space-sm);
  }

  .top-bar-left {
    flex-basis: auto;
  }

  .project-title {
    font-size: var(--app-font-size-xs);
  }

  .top-actions :deep(.top-icon-btn),
  .layout-toggle-btn {
    --n-width: 28px;
    --n-height-small: 28px;
    width: 28px;
    height: 28px;
  }

  .layout-toggle-group {
    height: 28px;
  }

  .toolbar-icon,
  .toolbar-icon svg {
    width: 20px;
    height: 20px;
    flex-basis: 20px;
  }

  .top-bar-right {
    --top-bar-right-gap: var(--app-space-md);
    flex-wrap: nowrap;
  }

  .top-bar-right :deep(.n-button) {
    --n-height-small: 24px;
    --n-padding: 0 var(--app-space-sm);
  }

  .top-bar-right :deep(.n-button__content),
  .top-actions :deep(.n-button__content) {
    font-size: 11px;
  }

  .user-trigger {
    min-height: 28px;
    padding: 0 2px;
    gap: 0;
  }

  .user-trigger :deep(.n-text) {
    display: none;
  }
}

@media (max-width: 720px) {
  .top-bar {
    padding: 5px var(--app-space-sm);
  }

  .top-bar-left {
    flex-basis: auto;
  }

  .top-actions {
    gap: var(--app-space-xs);
  }

  .top-bar-right {
    --top-bar-right-gap: var(--app-space-sm);
  }

  .user-trigger :deep(.n-text) {
    display: none;
  }
}

.top-bar--portrait {
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-rows: auto auto;
  gap: 4px var(--app-space-xs);
  padding: 4px var(--app-space-sm);
}

.top-bar--portrait .top-bar-left {
  grid-column: 1;
  grid-row: 1;
}

.top-bar--portrait .top-bar-right {
  grid-column: 2;
  grid-row: 1;
}

.top-bar--portrait .top-actions {
  grid-column: 1 / -1;
  grid-row: 2;
  justify-self: stretch;
  overflow-x: auto;
}

.top-bar--portrait .top-action-group {
  width: max-content;
}

.top-bar--portrait .project-title {
  font-size: var(--app-font-size-xs);
}

.top-bar--portrait .top-actions :deep(.top-icon-btn),
.top-bar--portrait .layout-toggle-btn {
  --n-width: 28px;
  --n-height-small: 28px;
  width: 28px;
  height: 28px;
}

.top-bar--portrait .layout-toggle-group {
  height: 28px;
}

.top-bar--portrait .toolbar-icon,
.top-bar--portrait .toolbar-icon svg {
  width: 20px;
  height: 20px;
  flex-basis: 20px;
}

.top-bar--portrait .top-bar-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  --top-bar-right-gap: var(--app-space-sm);
  gap: var(--top-bar-right-gap);
  min-width: 0;
  max-width: min(62vw, 320px);
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}

.top-bar--portrait .top-bar-right :deep(.n-button) {
  --n-height-small: 24px;
  --n-padding: 0 6px;
  flex-shrink: 0;
}

.top-bar--portrait .top-bar-right :deep(.n-button__content),
.top-bar--portrait .top-actions :deep(.n-button__content) {
  font-size: 11px;
}

/* 竖屏第一行：头像始终可见并靠前，昵称能放下则省略显示 */
.top-bar--portrait .user-trigger {
  display: inline-flex;
  flex: 0 1 auto;
  order: -1;
  min-width: 26px;
  max-width: min(38vw, 120px);
  min-height: 28px;
  padding: 0 2px;
  gap: 4px;
}

.top-bar--portrait .user-trigger :deep(.auth-avatar) {
  flex-shrink: 0;
}

.top-bar--portrait .user-trigger :deep(.n-text) {
  display: block;
  min-width: 0;
  max-width: 72px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 11px;
  line-height: 1.2;
}

.top-bar--portrait .user-offline {
  order: -1;
  flex-shrink: 0;
  font-size: 11px;
  white-space: nowrap;
}
</style>
